import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  importProvidersFrom,
  inject,
  Injectable,
  provideAppInitializer,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ModalEffects } from '@app/+state/modal';
import { appRoutes } from '@app/app-routing.module';
import { AppComponent } from '@app/app.component';
import { DataService } from '@app/core/services/data.service';
import { HarnessService } from '@app/core/services/harness.service';
import { SpinnerInterceptor } from '@app/interceptors/spinner.interceptor';
import { ENV_VARS, getEnvOrDefault } from '@app/shared/env';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { environment } from '@environments/environment';
import { CustomSerializer } from '@lemans/shared';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  ControlPageEffects,
  FeatureFlagsEffects,
  reducers,
  ReportEffects,
  SessionManagementEffects,
  SignalREffects,
  SnackBarEffects,
  SystemAdministrationEffects,
  TenantEffects,
  TrialsEffects,
  UserEffects,
} from '@ra-state';
import {
  ConsoleLogger,
  DomHandler,
  LOGGER,
  RaUiButtonModule,
  TypographyService,
} from '@ra-web-tech-ui-toolkit/components';
import { SkyhookDndModule } from '@ra-web-tech-ui-toolkit/skyhook-core';
import { createDefaultMultiBackend, HTML5ToTouch } from '@ra-web-tech-ui-toolkit/skyhook-multi-backend';
import { LayoutModule, MultiTranslateHttpLoader, TranslationAsset } from '@rockwell-automation-inc/layout';
import {
  AuthErrorInterceptor,
  AuthService,
  ConfigService,
  CoreConfiguration,
  ServiceConfigProvider,
  ServiceModule,
} from '@rockwell-automation-inc/service';
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  DateFilterModule,
  EventApiModule,
  ModuleRegistry,
  NumberFilterModule,
  PaginationModule,
  RowApiModule,
  RowAutoHeightModule,
  RowSelectionModule,
  RowStyleModule,
  TextFilterModule,
  ValidationModule,
} from 'ag-grid-community';
import {
  CellSelectionModule,
  ClipboardModule,
  ColumnsToolPanelModule,
  LicenseManager,
  MenuModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
} from 'ag-grid-enterprise';

@Injectable({ providedIn: 'root' })
export class ServiceConfigFromApp implements ServiceConfigProvider {
  private readonly configService = inject<ConfigService<CoreConfiguration>>(ConfigService);

  get CoreConfiguration(): CoreConfiguration {
    return this.configService.config;
  }
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient): MultiTranslateHttpLoader {
  const translationResouceList: TranslationAsset[] = [{ path: './assets/i18n/' }];
  return new MultiTranslateHttpLoader(http, translationResouceList);
}

export function initConfig(): void {
  const configService = inject(ConfigService<CoreConfiguration>);
  configService.setConfig(environment.appConfiguration);
}

LicenseManager.setLicenseKey(getEnvOrDefault(ENV_VARS.AG_GRID_KEY, 'invalid'));
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  PaginationModule,
  CellSelectionModule,
  ClipboardModule,
  SetFilterModule,
  MenuModule,
  ColumnsToolPanelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowAutoHeightModule,
  RowSelectionModule,
  RowApiModule,
  RowStyleModule,
  ColumnApiModule,
  EventApiModule,
  ValidationModule,
  CellStyleModule,
]);

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(appRoutes),
    importProvidersFrom(
      FormsModule,
      ReactiveFormsModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        fallbackLang: 'en-US',
      }),
      ServiceModule.forRoot(environment.appConfiguration),
      AuthModule.forRoot(),
      LayoutModule,
      MatSidenavModule,
      RaUiButtonModule,
      SkyhookDndModule.forRoot({
        backend: createDefaultMultiBackend(),
        options: HTML5ToTouch,
      }),
      MatDialogModule,
      StoreModule.forRoot(reducers),
      StoreDevtoolsModule.instrument({ logOnly: environment.production }),
      StoreRouterConnectingModule.forRoot({
        serializer: CustomSerializer,
      }),
      EffectsModule.forRoot([
        ControlPageEffects,
        SignalREffects,
        SnackBarEffects,
        UserEffects,
        TrialsEffects,
        ModalEffects,
        TenantEffects,
        FeatureFlagsEffects,
        SessionManagementEffects,
        ReportEffects,
        SystemAdministrationEffects,
      ]),
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    TypographyService,
    DomHandler,
    {
      provide: Renderer2,
      deps: [RendererFactory2],
      useFactory: (rendererFactory: RendererFactory2): Renderer2 => rendererFactory.createRenderer(null, null),
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: {
        usePopover: false, // Restores classic z-index / .cdk-overlay-container behavior
      },
    },
    DataService,
    AuthService,
    HarnessService,
    provideAppInitializer(initConfig),
    { provide: LOGGER, useClass: ConsoleLogger },
    { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SpinnerInterceptor, multi: true },
  ],
}).catch((err) => {
  // NOSONAR
  // eslint-disable-next-line no-console
  console.error(err);
});
